import {AxiosInstance} from 'axios'
import {getAuth} from './AuthHelpers'
import {clearSessionAndRedirect, requestTokenRefresh} from './TokenRefresh'

type AttachAuthInterceptorsOptions = {
  showSessionExpiredToast?: boolean
  refreshOn402?: boolean
}

const isRefreshRequest = (url: string | undefined): boolean => {
  return Boolean(url?.includes('/accounts/refresh-token'))
}

const shouldAttemptRefresh = (status: number | undefined, refreshOn402: boolean): boolean => {
  if (status === 401) {
    return true
  }

  return refreshOn402 && status === 402
}

const showSessionExpiredMessage = (showSessionExpiredToast: boolean) => {
  if (!showSessionExpiredToast) {
    return
  }

  import('../../../../utils/toaster').then((module) => {
    module.default('error', 'Session expired. Please login again.')
  })
}

export const attachAuthInterceptors = (
  axiosInstance: AxiosInstance,
  options: AttachAuthInterceptorsOptions = {}
) => {
  const {showSessionExpiredToast = false, refreshOn402 = false} = options

  axiosInstance.interceptors.request.use(
    (config) => {
      const auth = getAuth()
      if (auth?.token && config.headers) {
        config.headers.Authorization = `Bearer ${auth.token}`
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config
      const status = error.response?.status

      if (
        originalRequest &&
        !originalRequest._retry &&
        !isRefreshRequest(originalRequest.url) &&
        shouldAttemptRefresh(status, refreshOn402)
      ) {
        originalRequest._retry = true

        const refreshedAuth = await requestTokenRefresh()
        if (refreshedAuth?.token) {
          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers.Authorization = `Bearer ${refreshedAuth.token}`
          return axiosInstance(originalRequest)
        }
      }

      if (status === 401 || (refreshOn402 && status === 402)) {
        showSessionExpiredMessage(showSessionExpiredToast)
        clearSessionAndRedirect()
      }

      if (status === 503) {
        if (showSessionExpiredToast) {
          import('../../../../utils/toaster').then((module) => {
            module.default('error', 'Service Unavailable. Please login again.')
          })
        }
        clearSessionAndRedirect()
      }

      return Promise.reject(error)
    }
  )
}
